import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const LAST_UPDATED = "June 2, 2026";

export default function Terms() {
  useEffect(() => {
    document.title = "Terms of Service — LumberLoop USA";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20" data-testid="terms-page">
      <div className="label-tag">Legal</div>
      <h1 className="font-heading text-5xl md:text-6xl font-black uppercase mt-2 leading-none text-[#1A365D]">
        Terms of Service
      </h1>
      <p className="mt-3 font-mono-ll text-xs tracking-widest text-slate-500 uppercase">Last updated: {LAST_UPDATED}</p>

      <div className="brutal-card p-8 md:p-10 mt-10 space-y-8 text-slate-800 leading-relaxed">

        <Section title="1. Agreement">
          <p>
            These Terms of Service ("Terms") govern your access to and use of the LumberLoop USA website and services (the "Service"), operated by LumberLoop USA ("we," "us," "our"). By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
          </p>
        </Section>

        <Section title="2. Eligibility">
          <p>
            You must be at least 18 years old and legally able to enter into contracts to use the Service. By using the Service you represent that you meet these requirements and that any information you submit is your own, or that you have authorization to submit it on behalf of a company you represent.
          </p>
        </Section>

        <Section title="3. Accounts">
          <p>
            Some features (such as the manufacturer dashboard) require an account. You agree to provide accurate registration information and keep it current. You are responsible for safeguarding your password and for all activity that occurs under your account. Notify us immediately at info@lumberloopusa.com if you suspect unauthorized access.
          </p>
        </Section>

        <Section title="4. The Service">
          <p>LumberLoop USA is a lead-routing platform that:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Accepts contractor requests for building materials,</li>
            <li>Matches those requests to dealers in the contractor's geographic market based on ZIP code and state, and</li>
            <li>Notifies the parent manufacturer of the matched dealer(s) by email so they can follow up directly with the contractor.</li>
          </ul>
          <p className="mt-3">
            We do not buy, sell, deliver, or warrant any building materials. We are not a party to any transaction between a contractor and a dealer or manufacturer. Our role is limited to routing the lead.
          </p>
        </Section>

        <Section title="5. Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Submit false, misleading, or fraudulent leads or registrations;</li>
            <li>Use another person's or company's contact information without permission;</li>
            <li>Scrape, copy, or republish content or data from the Service;</li>
            <li>Reverse-engineer, decompile, or attempt to access non-public parts of the Service;</li>
            <li>Use the Service to send spam, harass, or harm any person or business;</li>
            <li>Use the Service in violation of any applicable law.</li>
          </ul>
        </Section>

        <Section title="6. Manufacturer & Dealer Content">
          <p>
            If you upload products, dealer information, or other content to the Service (collectively, "Manufacturer Content"), you represent that you have the right to do so and grant LumberLoop USA a non-exclusive, royalty-free license to display, distribute, and route that content as necessary to operate the Service. You remain solely responsible for the accuracy and lawfulness of the content you submit.
          </p>
        </Section>

        <Section title="7. Lead Routing — No Guarantees">
          <p>
            We use commercially reasonable efforts to route leads accurately and promptly, but the Service is provided on a best-effort basis. We do not guarantee that any particular lead will be received, that any dealer or manufacturer will respond, or that any transaction will result.
          </p>
        </Section>

        <Section title="8. Intellectual Property">
          <p>
            The LumberLoop USA name, logo, website, source code, and all related materials are the property of LumberLoop USA and are protected by copyright, trademark, and other laws. You may not use our marks or content without our prior written permission, except as expressly permitted by these Terms.
          </p>
        </Section>

        <Section title="9. Termination">
          <p>
            We may suspend or terminate your account at any time, with or without notice, if we believe you have violated these Terms or for any other reason at our discretion. You may stop using the Service at any time by contacting us to delete your account. Provisions of these Terms that by their nature should survive termination will survive.
          </p>
        </Section>

        <Section title="10. Disclaimer of Warranties">
          <p className="uppercase font-semibold text-sm">
            The service is provided "as is" and "as available," without warranties of any kind, express or implied, including warranties of merchantability, fitness for a particular purpose, non-infringement, accuracy, or availability. Your use of the service is at your own risk.
          </p>
        </Section>

        <Section title="11. Limitation of Liability">
          <p className="uppercase font-semibold text-sm">
            To the maximum extent permitted by law, LumberLoop USA and its officers, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, arising out of or related to your use of the service. Our total aggregate liability under these terms shall not exceed one hundred US dollars ($100).
          </p>
        </Section>

        <Section title="12. Indemnification">
          <p>
            You agree to indemnify and hold harmless LumberLoop USA from any claims, damages, liabilities, costs, or expenses (including reasonable attorneys' fees) arising out of your use of the Service, your violation of these Terms, or your violation of any rights of a third party.
          </p>
        </Section>

        <Section title="13. Changes to These Terms">
          <p>
            We may revise these Terms at any time by posting an updated version on this page. Material changes will be announced at the top of the page. Continued use of the Service after changes become effective constitutes your acceptance.
          </p>
        </Section>

        <Section title="14. Governing Law">
          <p>
            These Terms are governed by the laws of the United States of America and the state in which LumberLoop USA is principally based, without regard to conflict-of-law rules. You agree to the exclusive jurisdiction of the federal and state courts located in that state for any dispute arising under or in connection with these Terms.
          </p>
        </Section>

        <Section title="15. Contact">
          <p>
            Questions about these Terms? Email us at <a href="mailto:info@lumberloopusa.com" className="text-[#C62828] font-bold hover:underline">info@lumberloopusa.com</a>.
          </p>
        </Section>

      </div>

      <div className="mt-8 text-sm text-slate-500">
        See also: <Link to="/privacy" className="text-[#C62828] font-bold hover:underline" data-testid="terms-to-privacy">Privacy Policy</Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-xl font-bold uppercase text-[#1A365D] mb-3 border-l-4 border-[#C62828] pl-3">{title}</h2>
      <div className="text-base">{children}</div>
    </section>
  );
}
